import React, { useState, useEffect } from "react";
import PageTitle from "../../components/PageTitle";
import { toggleMenu } from "../../utils/helpers";
import { getApi } from "../../utils/api";
import {
  API_GET_ALL_PROJECTS_PROJECTID_AND_PROJECTNAME,
  API_DOWNLOAD_ALL_PROJECTS_REPORT,
  API_DOWNLOAD_PROJECT_REPORT_BY_PROJECT_ID,
  API_DOWNLOAD_PROJECT_REPORT_BY_EMPLOYEE_ID,
  API_SHADOW_RESOURCES_REPORTS,
  API_GET_SHADOW_NO_PROJECTS_REPORT,
} from "../../config/Endpoints";
import Cookies from "js-cookie";
import "react-datepicker/dist/react-datepicker.css";
import { showError } from "../../v2/utils/toast.ts";
import { useEmployeeStore } from "../../stores/employeeListStore";
import { IoInformationCircleSharp } from "react-icons/io5";
import { ThreeDots } from "react-loader-spinner";

const ProjectReport = ({ title, description }) => {
  const [projectList, setProjectList] = useState([]);
  const { employeeList, fetchEmployees, loading } = useEmployeeStore();
  const [selectedFilter, setSelectedFilter] = useState({
    value: "project",
    label: "Project Name",
  });
  const [secondDropdownValue, setSecondDropdownValue] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isLoading, setIsLoading] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const [startDate, endDate] = dateRange;
  const [statusFilter, setStatusFilter] = useState();
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const filterOptions = [
    { value: "employee", label: "Employee Name" },
    { value: "project", label: "Project Name" },
    { value: "projectCount", label: "Project Count" },
    { value: "shadow", label: "Shadow Resources" },
  ];

  // fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await getApi(
          API_GET_ALL_PROJECTS_PROJECTID_AND_PROJECTNAME,
          accessToken
        );

        const projects = (response.data?.data || []).map((p) => ({
          id: p.projectId,
          projectName: p.projectName,
        }));
        const projectOptions = projects.map((p) => ({
          value: p.id,
          label: p.projectName,
        }));
        setProjectList(projectOptions);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSubmit = async () => {
    try {
      setIsButtonLoading(true);
      let apiUrl = API_DOWNLOAD_ALL_PROJECTS_REPORT;
      const params = new URLSearchParams();

      if (
        selectedFilter?.value === "projectCount" &&
        secondDropdownValue?.value
      ) {
        switch (secondDropdownValue.value) {
          case "1-3":
            apiUrl = `${API_SHADOW_RESOURCES_REPORTS}/1to3Projects/export`;
            break;
          case "4-6":
            apiUrl = `${API_SHADOW_RESOURCES_REPORTS}/4to6Projects/export`;
            break;
          case "moreThan6":
            apiUrl = `${API_SHADOW_RESOURCES_REPORTS}/MoreThan6Projects/export`;
            break;
          case "noProjects":
            apiUrl = `${API_GET_SHADOW_NO_PROJECTS_REPORT}HaveNoProjects/export`;
            break;
          default:
            apiUrl = `${API_DOWNLOAD_ALL_PROJECTS_REPORT}`;
            break;
        }
      }

      if (
        selectedFilter?.value === "employee" &&
        secondDropdownValue?.value &&
        secondDropdownValue.value !== "all"
      ) {
        apiUrl = `${API_DOWNLOAD_PROJECT_REPORT_BY_EMPLOYEE_ID}/${secondDropdownValue.value}/export`;
      } else if (
        selectedFilter?.value === "project" &&
        secondDropdownValue?.value &&
        secondDropdownValue.value !== "all"
      ) {
        apiUrl = `${API_DOWNLOAD_PROJECT_REPORT_BY_PROJECT_ID}/${secondDropdownValue.value}/employees/export`;
        if (statusFilter?.value && statusFilter.value !== "all") {
          params.append("status", statusFilter.value);
        }
      } else {
        if (statusFilter?.value && statusFilter.value !== "all") {
          params.append("status", statusFilter.value);
        }
      }

      if (selectedFilter?.value === "shadow") {
        apiUrl = `${API_SHADOW_RESOURCES_REPORTS}/Shadowresource/export`;

        if (statusFilter?.value && statusFilter.value !== "all") {
          params.append("status", statusFilter.value);
        }
      }

      if (startDate) {
        params.append("from", new Date(startDate).toISOString().split("T")[0]);
      }
      if (endDate) {
        params.append("to", new Date(endDate).toISOString().split("T")[0]);
      }

      const finalUrl = params.toString()
        ? `${apiUrl}?${params.toString()}`
        : apiUrl;

      const res = await fetch(finalUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403 || res.status === 404) {
          const errorData = await res.json();
          showError(errorData.message || "Something went wrong.");
          return;
        }
        const contentType = res.headers.get("Content-Type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await res.json();

          if (
            selectedFilter?.value === "projectCount" &&
            errorData.message?.toLowerCase().includes("no data")
          ) {
            showError(errorData.message || "No records available.");
            return;
          }
          showError(errorData.message || "Download failed.");
        }
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Determine filename based on selected filter
      let fileName = "";

      if (selectedFilter?.value === "shadow") {
        fileName = "Project_Report_Shadow_Resources_All.xlsx";
      } else if (
        selectedFilter?.value === "employee" &&
        (!secondDropdownValue || secondDropdownValue.value === "all")
      ) {
        fileName = "Project_Report_ALL_Employees.xlsx";
      } else if (
        selectedFilter?.value === "employee" &&
        secondDropdownValue?.label
      ) {
        const cleanLabel = secondDropdownValue.label
          .replace(/\s+/g, "_")
          .trim();
        fileName = `Project_Report_${cleanLabel}.xlsx`;
      } else if (
        selectedFilter?.value === "project" &&
        secondDropdownValue?.label
      ) {
        fileName = `Project_Report_${secondDropdownValue.label}.xlsx`;
      } else {
        const filterName =
          selectedFilter?.value && secondDropdownValue?.label
            ? `${selectedFilter.value}_${secondDropdownValue.label}`
            : "All";
        fileName = `Project_Report_${filterName}.xlsx`;
      }

      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(
        err.response?.data?.message ||
          "Something went wrong, please try again later"
      );
    } finally {
      setIsButtonLoading(false);

      setIsLoading(false);
    }
  };

  const getSecondDropdownOptions = () => {
    if (!selectedFilter) return [{ value: "all", label: "All" }];
    if (selectedFilter.value === "employee") return employeeList;
    if (selectedFilter.value === "project") return projectList;
    if (selectedFilter.value === "projectCount") {
      return [
        { value: "1-3", label: "1 to 3 Projects " },
        { value: "4-6", label: "4 to 6 Projects" },
        { value: "moreThan6", label: "More than 6 Projects" },
        { value: "noProjects", label: "No Active Projects" },
      ];
    }
    return [{ value: "all", label: "All" }];
  };

  const getSecondDropdownPlaceholder = () => {
    return "All";
  };

  return (
    <div className="wrapper-body">
      <div className="page-heading">
        <div id="showMenuBtn" className="collapse-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <PageTitle title={title} description={description} />
      </div>

      {isLoading ? (
        <div className="centered-loader">
          <ThreeDots
            height="100"
            width="100"
            radius="9"
            color="blue"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        </div>
      ) : (
        <div className="card p-5 rounded-lg">
          <form id="assign-asset" onSubmit={handleSubmit}>
            <div className="d-flex justify-content-center p-8">
              <div
                className="report-card p-4 col-md-6 col-sm-10 rounded-lg "
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                  background: "#fff",
                }}
              >
                {/* Title and Subtitle */}
                <div className="text-center mb-4">
                  <h5 className="fw-bold mb-1">
                    <i className="bi bi-file-earmark-text me-2"></i> Project
                    Performance Report
                  </h5>
                </div>

                {/* Filter Section */}
                <div
                  className="p-3 mb-3 rounded-lg"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <label className="fw-semibold mb-2 d-flex align-items-center gap-1">
                    <span>Select Filter</span>
                    <IoInformationCircleSharp size={16} />
                  </label>

                  <div className="d-flex gap-2">
                    <select
                      id="filter"
                      name="filter"
                      value={selectedFilter ? selectedFilter.value : "project"}
                      onChange={(e) => {
                        const selectedOption = filterOptions.find(
                          (opt) => opt.value === e.target.value
                        );
                        setSelectedFilter(selectedOption || null);
                        setStatusFilter(null);
                        if (selectedOption?.value === "projectCount") {
                          const defaultOption = {
                            value: "1-3",
                            label: "1 to 3 Projects",
                          };
                          setSecondDropdownValue(defaultOption);
                        } else {
                          setSecondDropdownValue(null);
                        }
                      }}
                    >
                      {filterOptions.map((item, index) => (
                        <option key={`filter-${index}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>

                    <select
                      id="secondDropdown"
                      name="secondDropdown"
                      value={
                        secondDropdownValue
                          ? String(secondDropdownValue.value)
                          : "all"
                      }
                      onChange={(e) => {
                        const selectedOption = getSecondDropdownOptions().find(
                          (opt) => String(opt.value) === e.target.value
                        );
                        setSecondDropdownValue(selectedOption || null);
                        if (!selectedOption || selectedOption.value === "all") {
                          setStatusFilter(null);
                        }
                      }}
                      disabled={isLoading || selectedFilter?.value === "shadow"}
                      style={{
                        backgroundColor:
                          isLoading || selectedFilter?.value === "shadow"
                            ? "#f1f1f1ff"
                            : "#fff",
                      }}
                    >
                      {selectedFilter?.value !== "projectCount" && (
                        <option value="all">
                          {getSecondDropdownPlaceholder()}
                        </option>
                      )}
                      {selectedFilter?.value === "project"
                        ? projectList.map((proj, index) => (
                            <option key={`proj-${index}`} value={proj.value}>
                              {proj.label}
                            </option>
                          ))
                        : getSecondDropdownOptions()
                            .filter((item) => item.value !== "all")
                            .map((item, index) => (
                              <option
                                key={`second-${index}`}
                                value={String(item.value)}
                              >
                                {item.label}
                              </option>
                            ))}
                    </select>
                  </div>
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="theme-button"
                      disabled={isButtonLoading}
                    >
                      {isButtonLoading ? (
                        <ThreeDots
                          height="20"
                          width="60"
                          radius="9"
                          color="#fff"
                          ariaLabel="three-dots-loading"
                          visible={true}
                        />
                      ) : (
                        "Download"
                      )}
                    </button>
                  </div>
                </div>

                {/* Third Row - Button */}
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProjectReport;
